// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

/**
 * Audio constraints validator for preventing echo/feedback in voice interviews
 */

export interface AudioConstraintsValidation {
  isValid: boolean;
  warnings: string[];
  settings: MediaTrackSettings;
  recommendations: string[];
}

/**
 * Validates that audio track has proper constraints to prevent agent voice feedback
 */
export function validateAudioConstraints(audioTrack: MediaStreamTrack): AudioConstraintsValidation {
  const settings = audioTrack.getSettings();
  const warnings: string[] = [];
  const recommendations: string[] = [];

  // Echo cancellation is critical - prevents agent voice from being captured
  if (settings.echoCancellation !== true) {
    warnings.push('Echo cancellation is not enabled - agent voice may be picked up by microphone');
    recommendations.push('Request getUserMedia with echoCancellation: true');
  }

  // Noise suppression works with echo cancellation for optimal results
  if (settings.noiseSuppression !== true) {
    warnings.push('Noise suppression is not enabled - background noise may interfere');
    recommendations.push('Request getUserMedia with noiseSuppression: true');
  }

  // Auto gain control normalizes microphone volume
  if (settings.autoGainControl !== true) {
    warnings.push('Auto gain control is not enabled - microphone volume may be inconsistent');
    recommendations.push('Request getUserMedia with autoGainControl: true');
  }

  // 48kHz is optimal for voice quality
  if (settings.sampleRate && settings.sampleRate < 44100) {
    warnings.push(`Sample rate is low (${settings.sampleRate} Hz) - audio quality may be degraded`);
    recommendations.push('Request getUserMedia with sampleRate: 48000');
  }

  const isValid = settings.echoCancellation === true && settings.noiseSuppression === true;

  return {
    isValid,
    warnings,
    settings,
    recommendations
  };
}

/**
 * Returns optimal audio constraints for interview scenarios
 * Browser's WebRTC AEC removes agent voice before it reaches the application
 */
export function getOptimalAudioConstraints(): MediaStreamConstraints {
  return {
    audio: {
      echoCancellation: true, // Filters out agent voice from speakers
      noiseSuppression: true, // Reduces background noise
      autoGainControl: true, // Normalizes microphone volume
      sampleRate: 48000 // High quality for voice
    }
  };
}
